/**
 * Status enums, color map, and label map for OA Workflow tasks.
 */

export const BRAND_COLORS = {
    PRIMARY: "#005CB9",
    PRIMARY_HOVER: "#004B99",
    SECONDARY_HOVER: "#F2F7FC",
    TEXT_PLACEHOLDER: "#94A3B8",
    BORDER_LIGHT: "#E2E8F0"
};

export const TASK_STATUSES = {
    TO_DO: "To Do",
    IN_PROGRESS: "In Progress",
    ASSIGN_QA: "Assign QA",
    QC_PENDING: "QC Pending",
    UNDER_REVIEW: "Under Review",
    COMPLETED: "Completed"
};

export const PACKAGE_STATUSES = {
    PENDING: "Pending",
    PASS: "Pass",
    FAILED: "Failed"
};

export const STATUS_COLOR_MAP = {
    [TASK_STATUSES.TO_DO]: { bg: BRAND_COLORS.SECONDARY_HOVER, text: BRAND_COLORS.PRIMARY },
    [TASK_STATUSES.IN_PROGRESS]: { bg: "#FFF3E0", text: "#E65100" },
    [TASK_STATUSES.ASSIGN_QA]: { bg: "#FFEBEE", text: "#D32F2F" },
    [TASK_STATUSES.QC_PENDING]: { bg: "#F3E5F5", text: "#7B1FA2" },
    [TASK_STATUSES.UNDER_REVIEW]: { bg: "#FFF8E1", text: "#F57F17" },
    [TASK_STATUSES.COMPLETED]: { bg: "#E8F5E9", text: "#2E7D32" }
};

export const PACKAGE_STATUS_COLOR_MAP = {
    [PACKAGE_STATUSES.PENDING]: { bg: "#FFF3E0", text: "#E65100" },
    [PACKAGE_STATUSES.PASS]: { bg: "#E8F5E9", text: "#2E7D32" },
    [PACKAGE_STATUSES.FAILED]: { bg: "#FFEBEE", text: "#C62828" }
};

export const TAB_STYLES = {
    "& .MuiTab-root": {
        minHeight: 36,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: "14px",
        px: 2,
        minWidth: 'auto',
        color: BRAND_COLORS.TEXT_PLACEHOLDER
    },
    "& .Mui-selected": {
        color: `${BRAND_COLORS.PRIMARY} !important`
    },
    "& .MuiTabs-indicator": {
        backgroundColor: BRAND_COLORS.PRIMARY
    }
};

export const TASK_TABLE_COLUMNS = [
    { id: "taskId", label: "OA Task ID", minWidth: 140 },
    { id: "applicationNo", label: "Application Number", minWidth: 160 },
    { id: "packageStatus", label: "Package", minWidth: 100 },
    { id: "activityName", label: "Activity Name", minWidth: 200 },
    { id: "oaMailDate", label: "OA Mail Date", minWidth: 120 },
    { id: "taskStatus", label: "Task Status", minWidth: 120 },
    { id: "docketNo", label: "Docket #", minWidth: 120 },
    { id: "clientType", label: "Client Type", minWidth: 120 },
    { id: "processorName", label: "Processor Name", minWidth: 180 },
    { id: "qaName", label: "QA Name", minWidth: 180 },
    { id: "createdAt", label: "Created Date & Time", minWidth: 170 }
];

/**
 * PGS Admin/AM view columns — matches Figma PGS task list.
 */
export const PGS_TASK_TABLE_COLUMNS = [
    { id: "taskId", label: "Task ID", minWidth: 140 },
    { id: "applicationNo", label: "Application/Patent No.", minWidth: 160 },
    { id: "activityName", label: "Activity", minWidth: 200 },
    { id: "requestedDate", label: "Requested Date", minWidth: 130 },
];

/**
 * PGS columns that appear AFTER the dynamic status/attachment columns.
 */
export const PGS_TASK_TABLE_COLUMNS_AFTER = [
    { id: "sourceFile", label: "Source File", minWidth: 180 },
    { id: "processorName", label: "Processor", minWidth: 180 },
    { id: "qaName", label: "QA", minWidth: 180 },
];

/**
 * Client/User view columns — Completed tab (per Figma).
 * Package column renders a download link when packages exist in task_package table.
 */
export const CLIENT_COMPLETED_COLUMNS = [
    { id: "applicationNo", label: "Application Number", minWidth: 160 },
    { id: "clientName", label: "Client Name", minWidth: 180 },
    { id: "docketNo", label: "Docket No.", minWidth: 160 },
    { id: "activityName", label: "Activity Name", minWidth: 200 },
    { id: "oaMailDate", label: "Received Date", minWidth: 120 },
    { id: "attorneyInitials", label: "Attorney Initials", minWidth: 140 },
    { id: "package", label: "Package", minWidth: 100 }
];

/**
 * Client/User view columns — Pending tab.
 * No task status shown to clients for pending tasks.
 */
export const CLIENT_PENDING_COLUMNS = [
    { id: "applicationNo", label: "Application Number", minWidth: 160 },
    { id: "clientName", label: "Client Name", minWidth: 180 },
    { id: "docketNo", label: "Docket No.", minWidth: 160 },
    { id: "activityName", label: "Activity Name", minWidth: 200 },
    { id: "oaMailDate", label: "Received Date", minWidth: 120 },
    { id: "attorneyInitials", label: "Attorney Initials", minWidth: 140 }
];

/**
 * Configurable request types for the "Submit a new request" flow.
 * Only enabled types are shown to users.
 * NOTE: In future this should be tenant-wise configurable (DB-backed).
 */
export const REQUEST_TYPES = [
    { id: "oa_shell", label: "OA Shell", enabled: true },
    { id: "proofreading", label: "Proofreading", enabled: true, pgs: true },
    { id: "pta", label: "PTA", enabled: true, pgs: true },
    { id: "coc", label: "Certificate of Correction", enabled: true, pgs: true },
];

/**
 * PGS support type labels mapped to API values.
 */
export const PGS_SUPPORT_TYPES = {
    proofreading: "Proofreading",
    pta: "PTA",
    coc: "CoC"
};

/**
 * Proofreading scope options for PGS request submission.
 */
export const PROOFREADING_SCOPES = [
    "Entire",
    "Front Page & Claims",
    "Front Page, Claims & Drawings"
];
